import i18n from '../../../../i18n/i18n';
import { env } from '../../../../utils/env';
import { shareMessage } from './inviteFriendsCatalog';

/**
 * Where a web player can send their invite link. Each target is the network's own public share
 * URL, opened in a new tab - no SDKs, no app review:
 *
 * - Facebook opens the post composer with the link attached ("share as post"). Facebook does not
 *   let a site pre-fill the post text; the preview card comes from the page's Open Graph tags
 *   (`public/index.html`).
 * - Messenger uses Facebook's Send dialog, which needs the app id (`FACEBOOK_APP_ID`); it is left
 *   out when there is none.
 * - Viber only exists as an app scheme, so it works where Viber is installed.
 */
export type ShareTargetKey =
	| 'facebook'
	| 'messenger'
	| 'whatsapp'
	| 'telegram'
	| 'viber'
	| 'x'
	| 'email';

export type ShareTarget = {
	key: ShareTargetKey;
	label: string;
	/** Translation key for a generic label; brand names have none and render as they are. */
	labelKey?: string;
	/** Brand colour of the round button. */
	color: string;
	url: (link: string) => string;
};

const enc = encodeURIComponent;
/** Resolved when a target is opened, never at module load. */
const inviteText = () =>
	i18n.t('T_SITE_INVITE_SHARE_TEXT', {
		defaultValue: 'Join me on Svara! Use my invite link to get started:',
	});
const inviteSubject = () =>
	i18n.t('T_SITE_INVITE_SHARE_SUBJECT', { defaultValue: 'Join me on Svara!' });

const facebookAppId = (): string | null => {
	try {
		return env('FACEBOOK_APP_ID', ' ')?.trim() || null;
	} catch {
		return null;
	}
};

const ALL_TARGETS: ShareTarget[] = [
	{
		key: 'facebook',
		label: 'Facebook',
		color: '#1877F2',
		url: link => `https://www.facebook.com/sharer/sharer.php?u=${enc(link)}`,
	},
	{
		key: 'messenger',
		label: 'Messenger',
		color: '#0084FF',
		url: link =>
			`https://www.facebook.com/dialog/send?app_id=${enc(facebookAppId() ?? '')}&link=${enc(link)}&redirect_uri=${enc(link)}`,
	},
	{
		key: 'whatsapp',
		label: 'WhatsApp',
		color: '#25D366',
		url: link => `https://wa.me/?text=${enc(shareMessage(link))}`,
	},
	{
		key: 'telegram',
		label: 'Telegram',
		color: '#229ED9',
		url: link => `https://t.me/share/url?url=${enc(link)}&text=${enc(inviteText())}`,
	},
	{
		key: 'viber',
		label: 'Viber',
		color: '#7360F2',
		url: link => `viber://forward?text=${enc(shareMessage(link))}`,
	},
	{
		key: 'x',
		label: 'X',
		color: '#000000',
		url: link => `https://twitter.com/intent/tweet?text=${enc(inviteText())}&url=${enc(link)}`,
	},
	{
		key: 'email',
		label: 'Email',
		labelKey: 'T_SITE_SHARE_EMAIL',
		color: '#6B1A10',
		url: link => `mailto:?subject=${enc(inviteSubject())}&body=${enc(shareMessage(link))}`,
	},
];

export const shareTargets = (): ShareTarget[] =>
	ALL_TARGETS.filter(target => target.key !== 'messenger' || !!facebookAppId());

/** Opens a share target. App schemes and mailto replace nothing; web share pages get a new tab. */
export const openShareTarget = (target: ShareTarget, link: string) => {
	const url = target.url(link);
	if (/^https?:/i.test(url)) {
		window.open(url, '_blank', 'noopener,noreferrer,width=640,height=640');
		return;
	}
	window.location.href = url;
};
